import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { CopyCheck, Mail, Phone, MapPin } from "lucide-react";

const STATUS_LABELS = {
  DUPLICATE_REVIEW: { label: "À examiner", cls: "bg-amber-100 text-amber-700" },
  ACCEPTED: { label: "Accepté (non facturable)", cls: "bg-red-100 text-red-700" },
  REJECTED: { label: "Rejeté (facturable)", cls: "bg-emerald-100 text-emerald-700" },
};

const FILTERS = [
  { key: "DUPLICATE_REVIEW", label: "À examiner" },
  { key: "", label: "Tous" },
  { key: "ACCEPTED", label: "Acceptés" },
  { key: "REJECTED", label: "Rejetés" },
];

export default function AdminDuplicates() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("DUPLICATE_REVIEW");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/duplicates", {
        params: filter ? { status: filter } : {},
      });
      setItems(data || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id, decision) => {
    setBusyId(id);
    try {
      await api.post(`/admin/duplicates/${id}/resolve`, { decision });
      toast.success(
        decision === "accept"
          ? "Doublon accepté — prospect non facturable, commission annulée."
          : "Signalement rejeté — prospect facturable."
      );
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Signalements de doublons</h1>
        <p className="mt-2 max-w-2xl text-neutral-500">
          Les auto-écoles peuvent signaler, dans les 48 h suivant la transmission, un prospect déjà connu.
          Si vous <strong>acceptez</strong>, le prospect devient non facturable et la commission liée est annulée.
          Si vous <strong>rejetez</strong>, le prospect reste facturable.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setFilter(f.key)}
              data-testid={`dup-filter-${f.key || "all"}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                filter === f.key
                  ? "bg-neutral-900 text-white"
                  : "border border-neutral-300 text-neutral-600 hover:border-neutral-900"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4" data-testid="admin-duplicates-list">
          {items.length === 0 && (
            <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500">
              Aucun signalement pour ce filtre.
            </div>
          )}

          {items.map((d) => {
            const st = STATUS_LABELS[d.status] || { label: d.status, cls: "bg-neutral-100 text-neutral-600" };
            return (
              <div
                key={d.id}
                className="rounded-xl border border-neutral-200 bg-white p-5"
                data-testid={`dup-row-${d.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <CopyCheck className="h-5 w-5 text-brand" />
                      <span className="text-lg font-bold text-neutral-900">{d.candidate_name}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-neutral-600">
                      {d.candidate_email && (
                        <span className="flex items-center gap-1.5">
                          <Mail className="h-4 w-4 text-neutral-400" /> {d.candidate_email}
                        </span>
                      )}
                      {d.candidate_phone && (
                        <span className="flex items-center gap-1.5">
                          <Phone className="h-4 w-4 text-neutral-400" /> {d.candidate_phone}
                        </span>
                      )}
                      {d.candidate_city && (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-4 w-4 text-neutral-400" /> {d.candidate_city}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                  <p className="text-neutral-600">
                    Auto-école : <strong>{d.school_name}</strong>
                  </p>
                  <p className="text-neutral-600">
                    Motif : <strong data-testid={`dup-reason-${d.id}`}>{d.reason_label}</strong>
                  </p>
                  <p className="text-neutral-600">Transmis le : {fmt(d.transmission_date)}</p>
                  <p className="text-neutral-600">Signalé le : {fmt(d.created_at)}</p>
                </div>

                {d.explanation && (
                  <p className="mt-3 rounded-md bg-neutral-50 px-3 py-2 text-sm italic text-neutral-600">
                    « {d.explanation} »
                  </p>
                )}

                {d.status === "DUPLICATE_REVIEW" ? (
                  <div className="mt-5 flex gap-3">
                    <button
                      onClick={() => resolve(d.id, "accept")}
                      disabled={busyId === d.id}
                      data-testid={`dup-accept-${d.id}`}
                      className="rounded-md bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Accepter le doublon
                    </button>
                    <button
                      onClick={() => resolve(d.id, "reject")}
                      disabled={busyId === d.id}
                      data-testid={`dup-reject-${d.id}`}
                      className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-bold text-neutral-700 hover:border-neutral-900 disabled:opacity-50"
                    >
                      Rejeter
                    </button>
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-neutral-400">
                    Arbitré le {fmt(d.resolved_at)}
                    {d.resolution_note ? ` — ${d.resolution_note}` : ""}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Shell>
  );
}
